import React from "react";
import { isEmpty, last } from "lodash";
import { styled } from "@mui/material/styles";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import { Box } from "@mui/material";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import Skeleton from "@mui/material/Skeleton";

import { Colors } from "@/theme";
import { XLABELS } from "../../helper";
import { getLocalisationVal } from "@/utils/localizationUtil";
import moment from "moment";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 16,
    borderBottomColor: Colors.TABLE_HEADER1,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: Colors.TABLE_HEADER1,
  },
  "&:nth-of-type(odd)": {
    backgroundColor: Colors.WHITE,
  },
}));

const TableBodyComponent = ({
  dateRange,
  localization,
  loading,
  marginHistory,
  soldStocks,
  products,
}) => {
  const localizationWithoutDecimal = {
    ...localization,
    currency_decimal: false,
  };

  const days = moment(dateRange[1]).diff(moment(dateRange[0]), "days") + 1;

  const getLocalDate = (date) => moment.utc(date).local();

  const getProductCost = (product, date, key) => {
    const history = marginHistory.find(
      (item) => item?.product?.id === product.id
    );
    const margins = history?.margins;
    const costsOfDate = margins
      ?.filter((margin) => getLocalDate(margin.time).isSame(date, "date"))
      ?.map((item) => item[key]);
    if (!isEmpty(costsOfDate)) {
      return [last(costsOfDate)];
    }
    const lastCost =
      margins
        ?.filter((margin) => getLocalDate(margin.time) < moment(date))
        ?.slice(-1)?.[0]?.[key] || margins[0]?.[key];
    return [lastCost];
  };

  const getProductMargin = (product, date) => {
    const history = marginHistory.find(
      (item) => item?.product?.id === product.id
    );
    const margins = history?.margins;
    const marginsOfDate = margins
      ?.filter((margin) => getLocalDate(margin.time).isSame(date, "date"))
      ?.map((item) => item.absolute_margin);
    if (!isEmpty(marginsOfDate)) {
      return [last(marginsOfDate)];
    }
    const lastMargin =
      margins
        ?.filter((margin) => getLocalDate(margin.time) < moment(date))
        ?.slice(-1)?.[0]?.absolute_margin || margins[0]?.absolute_margin;
    return [lastMargin];
  };

  const getProductVolume = (product, date, key) => {
    const soldStock = soldStocks.find(
      (item) => item?.product?.id === product.id
    );
    const volumes = soldStock?.sold_stocks?.volumes;
    const volumeOfDate = volumes?.find((item) =>
      getLocalDate(item.from).isSame(date, "date")
    )?.[key];
    return volumeOfDate;
  };

  const getProductWtdVolume = (product, date) => {
    const soldStock = soldStocks.find(
      (item) => item?.product?.id === product.id
    );
    const volumes = soldStock?.sold_stocks?.volumes;
    const startOfWeek = moment(date).startOf("isoWeek");
    const wtdVolumes = volumes.filter(
      (item) =>
        getLocalDate(item.from).isSameOrAfter(startOfWeek) &&
        getLocalDate(item.from).isSameOrBefore(date)
    );
    return wtdVolumes?.reduce(
      (total, item) => total + parseFloat(item.sold_stocks),
      0
    );
  };

  if (loading) {
    return (
      <TableBody>
        {Array.apply(null, Array(3))
          .map(function (y, i) {
            return i;
          })
          .map((id) => (
            <TableRow key={`skeleton-${id}`} sx={{ height: 80 }}>
              {Array.apply(null, Array(products.length * 6 + 1))
                .map(function (y, i) {
                  return i;
                })
                .map((cellId) => (
                  <StyledTableCell
                    key={cellId}
                    align={cellId === 0 ? "left" : "center"}
                  >
                    <Skeleton variant="rounded" height={30} />
                  </StyledTableCell>
                ))}
            </TableRow>
          ))}
      </TableBody>
    );
  }

  return (
    <TableBody>
      {Array.apply(null, Array(days))
        .map(function (y, i) {
          return days - 1 - i;
        })
        .map((diff, id) => (
          <StyledTableRow key={`item-${diff}`} sx={{ height: 80 }}>
            <StyledTableCell
              align="left"
              sx={{
                position: "sticky",
                left: 0,
                zIndex: 2,
                background: id % 2 === 0 ? Colors.WHITE : Colors.TABLE_HEADER1,
                borderRight: `1px solid ${Colors.GRAY_BORDER1_40}`,
              }}
            >
              {moment(dateRange[0]).add(diff, "days").format("DD MMM YYYY")}
            </StyledTableCell>
            {products.map((product) => (
              <React.Fragment key={`${diff}-${product.id}`}>
                <StyledTableCell
                  align="center"
                  sx={{ borderLeft: `1px solid ${Colors.GRAY_BORDER1_40}` }}
                >
                  {getProductCost(
                    product,
                    moment(dateRange[0]).add(diff, "days"),
                    "buy_price"
                  )
                    .map((price) => getLocalisationVal(localization, price))
                    .join(",")}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {getProductCost(
                    product,
                    moment(dateRange[0]).add(diff, "days"),
                    "sell_price"
                  )
                    .map((price) => getLocalisationVal(localization, price))
                    .join(",")}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {getProductMargin(
                    product,
                    moment(dateRange[0]).add(diff, "days")
                  )
                    .map((price) => getLocalisationVal(localization, price))
                    .join(",")}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {getLocalisationVal(
                    localizationWithoutDecimal,
                    getProductVolume(
                      product,
                      moment(dateRange[0]).add(diff, "days"),
                      "sold_stocks"
                    )
                  )}
                  L
                </StyledTableCell>
                <StyledTableCell align="center">
                  {getLocalisationVal(
                    localizationWithoutDecimal,
                    getProductVolume(
                      product,
                      moment(dateRange[0]).add(diff, "days"),
                      "closing_volumes"
                    )
                  )}
                  L
                </StyledTableCell>
              </React.Fragment>
            ))}
          </StyledTableRow>
        ))}
    </TableBody>
  );
};

export default TableBodyComponent;
